import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { RealtimeChannel } from "@supabase/supabase-js";
import { toast } from "@/hooks/use-toast";

export interface PresenceUser {
  id: string;
  name: string;
  photo_url: string | null;
  online_at: string;
}

export interface LocationPresence {
  users: PresenceUser[];
  count: number;
}

export const useLocationPresence = (
  locationType: "city" | "country" | "arena",
  locationId: string | null
) => {
  const { user, profile } = useAuthContext();
  const [presence, setPresence] = useState<LocationPresence>({ users: [], count: 0 });
  const [isTracking, setIsTracking] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Join presence channel
  const joinLocation = useCallback(async () => {
    if (!locationId || !user || !profile) return;

    const channelName = `presence:${locationType}:${locationId}`;
    
    // Leave existing channel if any
    if (channelRef.current) {
      await supabase.removeChannel(channelRef.current);
    }

    const channel = supabase.channel(channelName);

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const users: PresenceUser[] = [];
        
        Object.values(state).forEach((presences: any[]) => {
          presences.forEach((p) => {
            if (p.id !== user.id) {
              users.push({
                id: p.id,
                name: p.name,
                photo_url: p.photo_url,
                online_at: p.online_at,
              });
            }
          });
        });

        // Add current user to count
        setPresence({
          users,
          count: users.length + 1, // Include self
        });
      })
      .on("presence", { event: "join" }, async ({ newPresences }) => {
        // Notify when someone new joins (not ourselves)
        for (const presence of newPresences) {
          const p = presence as unknown as PresenceUser;
          if (p.id && p.id !== user.id) {
            // Show local toast
            toast({
              title: `${p.name} joined! 👋`,
              description: locationType === "arena" ? "New participant in the arena" : "Someone's exploring this city too",
            });

            // Send push notification to the joining user about our presence
            notifyLocationJoin(locationId, locationType, {
              id: user.id,
              name: profile.name,
              photo_url: profile.photo_url,
            }, p.id);
          }
        }
      })
      .on("presence", { event: "leave" }, ({ leftPresences }) => {
        console.log("User left:", leftPresences);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({
            id: user.id,
            name: profile.name,
            photo_url: profile.photo_url,
            online_at: new Date().toISOString(),
          });
          setIsTracking(true);
        }
      });

    channelRef.current = channel;
  }, [locationId, locationType, user, profile]);

  // Leave presence channel
  const leaveLocation = useCallback(async () => {
    if (channelRef.current) {
      await channelRef.current.untrack();
      await supabase.removeChannel(channelRef.current);
      channelRef.current = null;
      setIsTracking(false);
      setPresence({ users: [], count: 0 });
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);

  return {
    presence,
    isTracking,
    joinLocation,
    leaveLocation,
  };
};

// Helper function to send push notification when someone joins a location
async function notifyLocationJoin(
  locationId: string,
  locationType: "city" | "country" | "arena",
  joiner: { id: string; name: string; photo_url: string | null },
  targetUserId: string
) {
  try {
    await supabase.functions.invoke("push-notifications", {
      body: {
        action: "send",
        userId: targetUserId,
        notification: {
          type: "location_join",
          title: locationType === "arena" ? "🎙️ New Arena Participant" : "🌍 Someone's exploring nearby!",
          body: `${joiner.name} just joined${locationType === "arena" ? " the arena" : " your city"}`,
          data: {
            location_type: locationType,
            location_id: locationId,
            joiner_id: joiner.id,
            joiner_name: joiner.name,
            joiner_photo: joiner.photo_url,
          },
        },
      },
    });
  } catch (error) {
    console.error("Failed to send location join notification:", error);
  }
}

// Hook to get aggregated presence counts for multiple locations
export const useMultiLocationPresence = (
  locationType: "city" | "country" | "arena",
  locationIds: string[]
) => {
  const { user, profile } = useAuthContext();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [previewUsers, setPreviewUsers] = useState<Record<string, PresenceUser[]>>({});
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());

  useEffect(() => {
    if (!user || !profile || locationIds.length === 0) return;

    const setupChannels = async () => {
      // Clean up old channels
      for (const [id, channel] of channelsRef.current) {
        if (!locationIds.includes(id)) {
          await supabase.removeChannel(channel);
          channelsRef.current.delete(id);
        }
      }

      // Setup new channels
      for (const locationId of locationIds) {
        if (channelsRef.current.has(locationId)) continue;

        const channelName = `presence:${locationType}:${locationId}`;
        const channel = supabase.channel(channelName);

        channel
          .on("presence", { event: "sync" }, () => {
            const state = channel.presenceState();
            const users: PresenceUser[] = [];

            Object.values(state).forEach((presences: any[]) => {
              presences.forEach((p) => {
                users.push({
                  id: p.id,
                  name: p.name,
                  photo_url: p.photo_url,
                  online_at: p.online_at,
                });
              });
            });

            setCounts((prev) => ({
              ...prev,
              [locationId]: users.length,
            }));

            setPreviewUsers((prev) => ({
              ...prev,
              [locationId]: users.slice(0, 4),
            }));
          })
          .subscribe();

        channelsRef.current.set(locationId, channel);
      }
    };

    setupChannels();

    return () => {
      channelsRef.current.forEach((channel) => {
        supabase.removeChannel(channel);
      });
      channelsRef.current.clear();
    };
  }, [user, profile, locationIds, locationType]);

  return { counts, previewUsers };
};
